import type { TFunction } from 'i18next';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import {
  downloadValidationExport,
  VALIDATION_EXPORT_MAX_PRODUCTS,
  ValidationExportTooLargeError,
  type ValidationExportParams,
} from '../../services/enrichment-validation-export.service';

interface Props extends ValidationExportParams {
  /** Products under the current filters; `undefined` while the list is still loading. */
  count: number | undefined;
}

/**
 * "Export CSV" in the validated-products drawer. Downloads every product the drawer is filtered
 * to (tab, search, problem, field), not just the loaded page. Disabled — with a tooltip saying
 * why — while the count loads, when the list is empty, or when it's over the export cap.
 */
export function ValidationExportButton({ count, ...params }: Props) {
  const { t } = useTranslation('catalog-operations');
  const [isExporting, setIsExporting] = useState(false);
  const numberFormat = new Intl.NumberFormat();

  const disabledReason =
    count === undefined
      ? t('enrichmentValidation.drawer.export.disabled.loading')
      : count === 0
        ? t('enrichmentValidation.drawer.export.disabled.empty')
        : count > VALIDATION_EXPORT_MAX_PRODUCTS
          ? t('enrichmentValidation.drawer.export.disabled.tooMany', {
              max: numberFormat.format(VALIDATION_EXPORT_MAX_PRODUCTS),
            })
          : null;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { filename, productCount } = await downloadValidationExport(params);
      toast.success(
        productCount === null
          ? t('enrichmentValidation.drawer.export.success', { file: filename })
          : t('enrichmentValidation.drawer.export.successWithCount', {
              file: filename,
              count: productCount,
              formatted: numberFormat.format(productCount),
            }),
      );
    } catch (error) {
      toast.error(exportErrorMessage(error, t, numberFormat));
    } finally {
      setIsExporting(false);
    }
  };

  const button = (
    <Button
      type="button"
      data-testid="batch-detail-validation-drawer-export-button"
      variant="outline"
      size="sm"
      className={cn('h-8 text-xs', disabledReason ? 'cursor-not-allowed' : 'cursor-pointer')}
      disabled={disabledReason !== null || isExporting}
      isLoading={isExporting}
      onClick={() => {
        void handleExport();
      }}
    >
      {!isExporting && <Download className="size-4" aria-hidden="true" />}
      {isExporting
        ? t('enrichmentValidation.drawer.export.pending')
        : t('enrichmentValidation.drawer.export.button')}
    </Button>
  );

  if (!disabledReason) return button;
  // A disabled button fires no pointer events, so the tooltip hangs off a wrapper.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-not-allowed">{button}</span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Maps an export failure to toast copy: the cap with both numbers, a retryable "unavailable"
 *  for 5xx, else generic — a 400 here is a web-app bug, not user copy. */
function exportErrorMessage(
  error: unknown,
  t: TFunction<'catalog-operations'>,
  numberFormat: Intl.NumberFormat,
): string {
  if (error instanceof ValidationExportTooLargeError) {
    return error.totalElements === null
      ? t('enrichmentValidation.drawer.export.error.tooLargeNoCount', {
          max: numberFormat.format(error.maxRows),
        })
      : t('enrichmentValidation.drawer.export.error.tooLarge', {
          total: numberFormat.format(error.totalElements),
          max: numberFormat.format(error.maxRows),
        });
  }
  if (error instanceof ApiError && (error.statusCode ?? 0) >= 500) {
    return t('enrichmentValidation.drawer.export.error.unavailable');
  }
  return t('enrichmentValidation.drawer.export.error.generic');
}
